import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import axios from 'utils/axios';
import URL from 'config/url';
import axiosAdmin from 'utils/axiosAdmin';
import { adminUserLogin } from './admin';

const formatAgencyParam = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.length > 0 ? value.join(',') : '';
  return value;
};

export const getAgencies = createAsyncThunk('GET AGENCY', async (data, thunkAPI) => {
  const params = new URLSearchParams({
    page: data.page || 1,
    limit: data.limit || 10,
    text: data.text || '',
    startDate: data.startDate || '',
    endDate: data.endDate || '',
    planType: formatAgencyParam(data.planType),
    sortOrder: data.sortOrder || '',
    sortField: data.sortField || '',
    paymentStatus: formatAgencyParam(data.paymentStatus),
    accountStatus: formatAgencyParam(data.accountStatus ?? data.agencyStatus),
    mailgunStatus: formatAgencyParam(data.mailgunStatus),
    subscriptionStatus: formatAgencyParam(data.subscriptionStatus),
    solutionType: formatAgencyParam(data.solutionType),
    originCountry: formatAgencyParam(data.originCountry),
    isSSOEnabled: formatAgencyParam(data.isSSOEnabled),
    accountName: formatAgencyParam(data.accountName ?? data.agencyName),
    preferredGateway: formatAgencyParam(data.preferredGateway),
    currentGateway: formatAgencyParam(data.currentGateway ?? data.paymentGateway),
    lastGateway: formatAgencyParam(data.lastGateway),
    preferred3ds: formatAgencyParam(data.preferred3ds),
    current3ds: formatAgencyParam(data.current3ds),
    last3ds: formatAgencyParam(data.last3ds),
    isNonLogin: formatAgencyParam(data.isNonLogin ?? data.is_non_login),
    classification: formatAgencyParam(data.classification),
  });
  const response = await axiosAdmin.get(`${URL.GET_ACCOUNT_AGENCIES}?${params.toString()}`, thunkAPI);
  return response.data;
});

export const getAgencyDetails = createAsyncThunk('GET AGENCY DETAILS', async (data, thunkAPI) => {
  const response = await axios.get(`${URL.GET_AGENCY}/${data?.agencyId}`, thunkAPI);
  return response.data;
});

export const deleteAgency = createAsyncThunk('DELETE AGENCY', async (data, thunkAPI) => {
  const response = await axiosAdmin.delete(URL.DELETE_ACCOUNT_AGENCY, { account_id: data?.agencyId }, thunkAPI);
  return response.data;
});

export const getAgencyUsers = createAsyncThunk('GET AGENCY USERS', async (data, thunkAPI) => {
  const response = await axios.get(
    `${URL.AGENCY_USERS}?agencyId=${data?.agencyId}&status=${data?.status}&userType=${data?.userType}&page=${
      data.page
    }&pageSize=${data.pageSize}&limit=${data.limit}&text=${data.text || ''}&name=${data.name || ''}&email=${
      (data.email && encodeURIComponent(data.email)) || ''
    }`,
    thunkAPI
  );
  return response.data;
});

export const updateAgencyPaymentStatus = createAsyncThunk('UPDATE AGENCY PAYMENT STATUS', async (data, thunkAPI) => {
  const response = await axios.patch(URL.AGENCY_PAYMENT_STATUS, data, thunkAPI);
  return response.data;
});

export const updateTicketingPermission = createAsyncThunk('UPDATE TICKETING PERMISSION', async (data, thunkAPI) => {
  const response = await axios.patch(URL.AGENCY_FLIGHT_TICKET_PERMISSION, data, thunkAPI);
  return response.data;
});

export const getAgencySettings = createAsyncThunk('GET AGENCY SETTINGS', async (data, thunkAPI) => {
  const response = await axiosAdmin.get(
    `${URL.ACCOUNT_ADMIN_SETTINGS}/${data?.orgId}/accounts/${data?.agencyId}/admin-settings`,
    thunkAPI
  );
  return response.data;
});

export const getSubscriptionLimits = createAsyncThunk('GET SUBSCRIPTION LIMITS', async (data, thunkAPI) => {
  const response = await axiosAdmin.get(`${URL.SUBSCRIPTION_LIMITS}?account_id=${data?.accountId}`, thunkAPI);
  return response.data;
});

export const getAccountSubscription = createAsyncThunk('GET ACCOUNT SUBSCRIPTION', async (data, thunkAPI) => {
  const response = await axiosAdmin.get(`${URL.ACCOUNT_SUBSCRIPTIONS}?account_id=${data?.accountId}`, thunkAPI);
  return response.data;
});

export const updateSubscriptionLimits = createAsyncThunk('UPDATE SUBSCRIPTION LIMITS', async (data, thunkAPI) => {
  const response = await axiosAdmin.patch(
    URL.SUBSCRIPTION_LIMITS,
    { account_id: data.accountId, customers_limit: data.customers_limit, sales_agents_limit: data.sales_agents_limit },
    thunkAPI
  );
  if (response.data?.status === 'success') {
    thunkAPI.dispatch(getSubscriptionLimits({ accountId: data.accountId }));
  }
  return { ...response.data, _sentData: data };
});

export const updateAccountAdminSettings = createAsyncThunk('UPDATE ACCOUNT ADMIN SETTINGS', async (data, thunkAPI) => {
  const response = await axiosAdmin.patch(
    `${URL.ACCOUNT_ADMIN_SETTINGS}/${data.org_id}/accounts/${data.account_id}/admin-settings`,
    data.settings,
    thunkAPI
  );
  if (response.data?.status === 'success') {
    thunkAPI.dispatch(getAgencySettings({ agencyId: data.account_id, orgId: data.org_id }));
  }
  return { ...response.data, _settingKey: data.settingKey, _sentSettings: data.settings };
});

export const getMerchant = createAsyncThunk('GET MERCHANT', async (data, thunkAPI) => {
  const response = await axiosAdmin.get(
    `${URL.ACCOUNT_ADMIN_SETTINGS}/${data.org_id}/accounts/${data.account_id}/merchant`,
    thunkAPI
  );
  return response.data;
});

export const updateMerchant = createAsyncThunk('UPDATE MERCHANT', async (data, thunkAPI) => {
  const payload = {};
  if (data.merchant_name != null && String(data.merchant_name).trim() !== '') {
    payload.merchant_name = String(data.merchant_name).trim();
  } else {
    payload.merchant_name = "";
  }
  if (data.merchant_dns_subdomain != null && String(data.merchant_dns_subdomain).trim() !== '') {
    payload.merchant_dns_subdomain = String(data.merchant_dns_subdomain).trim().toLowerCase();
  } else {
    payload.merchant_dns_subdomain = "";
  }
  const response = await axiosAdmin.patch(
    `${URL.ACCOUNT_ADMIN_SETTINGS}/${data.org_id}/accounts/${data.account_id}/merchant`,
    payload,
    thunkAPI
  );
  if (response.data?.status === 'success') {
    thunkAPI.dispatch(getMerchant({ org_id: data.org_id, account_id: data.account_id }));
  }
  return { ...response.data, _sentData: payload };
});

export const updateAgencyStatus = createAsyncThunk('UPDATE AGENCY STATUS', async (data, thunkAPI) => {
  const response = await axios.patch(URL.AGENCY_STATUS, data, thunkAPI);
  return response.data;
});

export const updateDisallowSameDay = createAsyncThunk(
  'UPDATE AGENCY DIALLOW SAME DAY SETTINGS',
  async (data, thunkAPI) => {
    const response = await axios.patch(URL.AGENCY_SETTINGS, data, thunkAPI);
    return response.data;
  }
);

export const updateApiAccess = createAsyncThunk('UPDATE AGENCY API ACCESS SETTINGS', async (data, thunkAPI) => {
  const response = await axios.patch(URL.AGENCY_SETTINGS, data, thunkAPI);
  return response.data;
});

export const updateManageLocation = createAsyncThunk('UPDATE MANAGE LOCATION SETTINGS', async (data, thunkAPI) => {
  const response = await axios.patch(URL.AGENCY_SETTINGS, data, thunkAPI);
  return response.data;
});

export const updateAgencySettings = createAsyncThunk('UPDATE AGENCY SETTINGS', async (data, thunkAPI) => {
  const response = await axios.patch(URL.AGENCY_SETTINGS, data, thunkAPI);
  return response.data;
});

export const cancelAgencySubscription = createAsyncThunk('CANCEL AGENCY SUBSCRIPTION', async (data, thunkAPI) => {
  const response = await axiosAdmin.post(
    URL.AGENCY_CANCEL_SUBSCRIPTION,
    { account_id: data.agencyId, cancel_reason: data.cancelReason },
    thunkAPI
  );
  if (data.agencyId) {
    thunkAPI.dispatch(getSubscriptionLimits({ accountId: data.agencyId }));
    thunkAPI.dispatch(getAccountSubscription({ accountId: data.agencyId }));
  }
  return response.data;
});

export const cancelTrialSubscription = createAsyncThunk('CANCEL AGENCY TRIAL SUBSCRIPTION', async (data, thunkAPI) => {
  const response = await axiosAdmin.post(URL.AGENCY_TRIAL_CANCEL_SUBSCRIPTION, { account_id: data.agencyId }, thunkAPI);
  if (data.agencyId) {
    thunkAPI.dispatch(getSubscriptionLimits({ accountId: data.agencyId }));
    thunkAPI.dispatch(getAccountSubscription({ accountId: data.agencyId }));
  }
  return response.data;
});

export const updateAgencyUserStatus = createAsyncThunk('UPDATE AGENCY USER STATUS', async (data, thunkAPI) => {
  const response = await axios.patch(URL.AGENCY_USER_STATUS, data, thunkAPI);
  return response.data;
});

export const resendVerificationEmail = createAsyncThunk('RESEND VERIFICATION EMAIL', async (data, thunkAPI) => {
  const response = await axios.post(URL.AGENCY_USER_RESEND_EMAIL_VERIFICATION, data, thunkAPI);
  return response.data;
});

export const resendVerificationAgencyEmail = createAsyncThunk(
  'RESEND AGENCY VERIFICATION EMAIL',
  async (data, thunkAPI) => {
    const response = await axiosAdmin.post(
      `${URL.AGENCY_VERIFY_USER_RESEND_EMAIL}/${data?.agencyId}`,
      {},
      thunkAPI
    );
    return response.data;
  }
);

export const updateCruiseSetting = createAsyncThunk('ENABLE OR DISABLE CRUISE SERVICE', async (data, thunkAPI) => {
  const response = await axios.post(URL.UPDATE_CRUISE_SETTING, data, thunkAPI);
  return response.data;
});

export const updateResortSetting = createAsyncThunk('ENABLE OR DISABLE RESORT SERVICE', async (data, thunkAPI) => {
  const response = await axios.put(URL.UPDATE_RESORT_AGENCY_SETTING, data, thunkAPI);
  return response.data;
});

export const getAgentDetails = createAsyncThunk('GET AGENT DETAILS', async (data, thunkAPI) => {
  const response = await axios.get(
    `${URL.GET_AGENT_DETAILS}?agencyId=${data?.agencyId}&agentId=${data?.agentId}`,
    thunkAPI
  );
  return response.data;
});

export const getAssociatedAgentClients = createAsyncThunk('GET ASSOCIATED AGENT CLIENTS', async (data, thunkAPI) => {
  const response = await axios.get(
    `${URL.GET_ASSOCIATED_AGENTS_CLIENTS}?page=${data.page}&pageSize=${data.pageSize}&limit=${data.limit}&agencyId=${
      data?.agencyId
    }&agentId=${data?.agentId}&status=${data?.status}&associationStatus=${data?.associationStatus}&name=${
      data.name || ''
    }&email=${data.email || ''}`,
    thunkAPI
  );
  return response.data;
});

export const getAssociateClientList = createAsyncThunk('GET ASSOCIATE CLIENT LIST', async (data, thunkAPI) => {
  const params = new URLSearchParams({
    page: data.page || 1,
    limit: data.limit || 10,
  });
  if (data?.status) params.set('status', data.status);
  if (data?.associationStatus) params.set('association_status', data.associationStatus);
  if (data?.name) params.set('name', data.name);
  if (data?.email) params.set('email', data.email);

  const url = `${URL.ASSOCIATE_CLIENT_LIST}/${data.orgId}/accounts/${data.agencyId}/agents/${data.agentId}/clients?${params.toString()}`;
  const response = await axiosAdmin.get(url, thunkAPI);
  return response.data;
});

export const updateAgencyPaymentGateway = createAsyncThunk('UPDATE AGENCY PAYMENT GATEWAY', async (data, thunkAPI) => {
  const response = await axios.patch(URL.UPDATE_AGENCY_PAYMENT_GATEWAY, data, thunkAPI);
  return response.data;
});

export const updateEnableHotelsV2 = createAsyncThunk('UPDATE ENABLE HOTELS V2', async (data, thunkAPI) => {
  const response = await axiosAdmin.put(
    URL.UPDATE_ACCOUNT_AGENCY,
    { account_id: data.agencyId, enableHotelsV2: data.enableHotelsV2 },
    thunkAPI
  );
  return response.data;
});

export const updateEnableFlightsV2 = createAsyncThunk('UPDATE ENABLE FLIGHTS V2', async (data, thunkAPI) => {
  const response = await axiosAdmin.put(
    URL.UPDATE_ACCOUNT_AGENCY,
    { account_id: data.agencyId, enableFlightsV2: data.enableFlightsV2 },
    thunkAPI
  );
  return response.data;
});

export const updateEnableCarsV2 = createAsyncThunk('UPDATE ENABLE CARS V2', async (data, thunkAPI) => {
  const response = await axiosAdmin.put(
    URL.UPDATE_ACCOUNT_AGENCY,
    { account_id: data.agencyId, enableCarsV2: data.enableCarsV2 },
    thunkAPI
  );
  return response.data;
});

export const updateBitpayPaymentAllowed = createAsyncThunk('UPDATE BITPAY PAYMENT ALLOWED', async (data, thunkAPI) => {
  const response = await axios.patch(URL.UPDATE_BITPAY_PAYMENT_ALLOWED, data, thunkAPI);
  return response.data;
});

export const updateBNPLPaymentAllowed = createAsyncThunk('UPDATE BNPL PAYMENT ALLOWED', async (data, thunkAPI) => {
  const response = await axios.patch(URL.UPDATE_BNPL_PAYMENT_ALLOWED, data, thunkAPI);
  return response.data;
});

export const updateAgencyHotelVendor = createAsyncThunk('UPDATE AGENCY HOTEL VENDOR', async (data, thunkAPI) => {
  const response = await axios.patch(URL.UPDATE_AGENCY_HOTEL_VENDOR, data, thunkAPI);
  return response.data;
});

export const getHotelVendorList = createAsyncThunk('GET HOTEL VENDOR LIST', async (data, thunkAPI) => {
  const params = data?.account_id ? `?account_id=${data.account_id}&product_type=${data.product_type}` : '';
  const response = await axiosAdmin.get(`${URL.VENDOR_BY_ACCOUNT_ID}${params}`, thunkAPI);
  return { ...response.data, _productType: data.product_type };
});

export const updateAgencyHotelVendors = createAsyncThunk('UPDATE AGENCY HOTEL VENDORS', async (data, thunkAPI) => {
  const response = await axiosAdmin.put(
    URL.VENDOR_BY_ACCOUNT_ID,
    {
      account_id: data.account_id,
      product_type: data.product_type,
      vendor_id: data.vendor_id,
      enabled: data.enabled,
    },
    thunkAPI
  );
  return response.data;
});

export const updateOTPSignupStatus = createAsyncThunk('UPDATE HIDE HEADER STATUS', async (data, thunkAPI) => {
  const response = await axios.patch(URL.UPDATE_AGENCY_OTP_SIGNUP_STATUS, data, thunkAPI);
  return response.data;
});

export const updatHideHeaderStatus = createAsyncThunk('UPDATE OTP SIGNUP STATUS', async (data, thunkAPI) => {
  const response = await axios.patch(URL.UPDATE_AGENCY_HIDE_HEADER_STATUS, data, thunkAPI);
  return response.data;
});

export const updateExranetStatus = createAsyncThunk('UPDATE EXTRANET STATUS', async (data, thunkAPI) => {
  const response = await axios.patch(URL.UPDATE_AGENCY_EXTRANET_STATUS, data, thunkAPI);
  return response.data;
});

export const updateHideDealOrProposalOption = createAsyncThunk(
  'UPDATE HIDE DEAL OR PROPOSAL',
  async (data, thunkAPI) => {
    const response = await axios.patch(URL.UPDATE_AGENCY_HIDE_DEAL_OR_PROPOSAL_OPTION, data, thunkAPI);
    return response.data;
  }
);

export const updateAllowNonLoginFlow = createAsyncThunk('UPDATE ALLOW NON LOGIN FLOW', async (data, thunkAPI) => {
  const response = await axios.patch(URL.UPDATE_AGENCY_ALLOW_NON_LOGIN_FLOW, data, thunkAPI);
  return response.data;
});

export const updateAgencyViewVendorName = createAsyncThunk('UPDATE AGENCY VIEW VENDOR NAME', async (data, thunkAPI) => {
  const response = await axios.patch(URL.UPDATE_AGENCY_VIEW_VENDOR_NAME, data, thunkAPI);
  return response.data;
});

export const updateDisplayTotalPriceWithAdditionalCharges = createAsyncThunk(
  'UPDATE DISPLAY TOTAL PRICE WITH ADDITIONAL CHARGES',
  async (data, thunkAPI) => {
    const response = await axios.patch(URL.UPDATE_AGENCY_DISPLAY_TOTAL_PRICE_WITH_ADDITIONAL_CHARGES, data, thunkAPI);
    return response.data;
  }
);
export const updateAgencyEnableAllNewUSer = createAsyncThunk('ENABLE ALL NEW USER', async (data, thunkAPI) => {
  const response = await axios.patch(URL.AGENCY_ENABLE_ALL_NEW_USER, data, thunkAPI);
  return response.data;
});

export const updateCancellationEmail = createAsyncThunk('UPDATE AGENCY SETTING', async (data, thunkAPI) => {
  const response = await axios.patch(URL.AGENCY_SETTINGS, data, thunkAPI);
  return response.data;
});
export const updateAgencySettingsData = createAsyncThunk('UPDATE AGENCY SETTINGS DATA', async (data, thunkAPI) => {
  const response = await axios.patch(URL.UPDATE_AGENCY_SETTINGS, data, thunkAPI);
  return response.data;
});

export const getAccountUserStats = createAsyncThunk('GET ACCOUNT USER STATS', async (data, thunkAPI) => {
  const response = await axiosAdmin.get(
    `${URL.ACCOUNT_USER_STATS}/${data?.orgId}/accounts/${data?.accountId}/user-stats`,
    thunkAPI
  );
  return response.data;
});

export const getClientUsers = createAsyncThunk('GET CLIENT USERS', async (data, thunkAPI) => {
  const params = new URLSearchParams({
    type: 'client',
    page: data?.page || 1,
    limit: data?.limit || 10,
    status: data?.status,
    ...(data?.searchQuery && { searchQuery: data.searchQuery }),
  });
  const response = await axiosAdmin.get(
    `${URL.CLIENT_USERS}/${data?.orgId}/accounts/${data?.agencyId}/users?${params.toString()}`,
    thunkAPI
  );
  return response.data;
});

export const getAgentUsers = createAsyncThunk('GET AGENT USERS', async (data, thunkAPI) => {
  const params = new URLSearchParams({
    type: 'agent',
    page: data?.page || 1,
    limit: data?.limit || 10,
    ...(data?.status && { status: data.status }),
    ...(data?.searchQuery && { searchQuery: data.searchQuery }),
  });
  const response = await axiosAdmin.get(
    `${URL.AGENT_USERS}/${data?.orgId}/accounts/${data?.agencyId}/users?${params.toString()}`,
    thunkAPI
  );
  return response.data;
});

export const updateUserStatus = createAsyncThunk('UPDATE USER STATUS', async (data, thunkAPI) => {
  const response = await axiosAdmin.put(
    `${URL.UPDATE_USER_STATUS}/${data?.orgId}/accounts/${data?.account_id}/users/${data?.id}`,
    { id: data?.id, status: data?.status, user_type: data?.user_type },
    thunkAPI
  );
  return { ...response.data, _id: data?.id, _newStatus: data?.status };
});

export const getAgentAssociateClientData = createAsyncThunk(
  'GET AGENT ASSOCIATE CLIENT DATA',
  async (data, thunkAPI) => {
    const response = await axiosAdmin.get(
      `${URL.AGENT_ASSOCIATE_CLIENT_DATA}/${data?.orgId}/accounts/${data?.agencyId}/agents/${data?.agentId}/clients/stats`,
      thunkAPI
    );
    return response.data;
  }
);

export const getAdminCommissionSummary = createAsyncThunk('GET ADMIN COMMISSION SUMMARY', async (data, thunkAPI) => {
  const response = await axiosAdmin.get(
    `${URL.ADMIN_COMMISSION_SUMMARY}/${data?.orgId}/accounts/${data?.accountId}/booking-stats`,
    thunkAPI
  );
  return response.data;
});

const initialState = {
  isLoading: false,
  isUserLoading: false,
  isPaymentStatusLoading: false,
  isAgencyStatusLoading: false,
  isPaymentGatewayLoading: false,
  isBitpayPaymentLoading: false,
  isBnplLoading: false,
  isSameDayAllowBookingLoading: false,
  isCancelSubscriptionLoading: false,
  agency: null,
  users: null,
  agencySettings: null,
  isUserUpdateLoading: false,
  isApiAccessLoading: false,
  isFlightTicketingLoading: false,
  isUserLimitLoading: false,
  agentDetails: null,
  associatedUsers: null,
  isManageLocationLoading: false,
  isHotelVendorLoading: false,
  OTPSignupEnableLoading: false,
  hideHeaderEnableLoading: false,
  extranetEnabled: false,
  hideDealOrProposalLoading: false,
  allowNonLoginFlowLoading: false,
  agencyViewVendorNameLoading: false,
  cancellationDisplayOnEmail: false,
  enableAllNewUserLoading: false,
  displayTotalPriceWithAdditionalChargesLoading: false,
  isAgencySettingsDataLoading: false,
  isAdminSettingsUpdating: false,
  isMerchantUpdating: false,
  isMerchantLoading: false,
  isMerchantFetched: false,
  merchantDetails: null,
  merchantAccountId: null,
  updatingSettingKey: null,
  settingsRollbackSnapshot: null,
  isSettingsLoading: false,
  settingsError: null,
  hotelVendorList: [],
  isHotelVendorListLoading: false,
  updatingVendorId: null,
  accountVendors: {
    HOTEL: [],
    FLIGHT: [],
  },
  isAccountVendorsLoading: {
    HOTEL: false,
    FLIGHT: false,
  },
  updatingAccountVendor: null,
  accountVendorsRollback: null,
  subscriptionLimitsRollback: null,
  updatingLimitKey: null,
  isSubscriptionLimitsUpdating: false,
  accountSubscription: null,
  isAccountSubscriptionLoading: false,
  commissionSummary: null,
  isCommissionSummaryLoading: false,
  userStats: null,
  isUserStatsLoading: false,
  clientUsers: null,
  isClientUsersLoading: false,
};

export const agencySlice = createSlice({
  name: 'agency',
  initialState,
  reducers: {},
  extraReducers: {
    [updateCruiseSetting.pending]: (state) => {
      state.isLoading = true;
    },
    [updateCruiseSetting.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.agencySettings = {
        ...state.agencySettings,
        agencySettings: {
          ...state.agencySettings.agencySettings,
          isCruiseEnable: action.payload.data.isEnabled,
        },
      };
      toast.success(action?.payload?.message);
    },
    [updateCruiseSetting.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action?.payload?.message);
    },
    // get agencies
    [getAgencies.pending]: (state) => {
      state.isLoading = true;
      state.agency = null;
    },
    [getAgencies.fulfilled]: (state, action) => {
      state.isLoading = false;
      const data = action.payload.data;
      state.agency = {
        agencies: (data.accounts || []).map((acc) => ({
          ...acc,
          agencyName: acc.account_name,
          agencyStatus: acc.account_status,
        })),
        agencyCount: data.totalCount || 0,
        originCountries: data.originCountries || [],
      };
    },
    [getAgencies.rejected]: (state, action) => {
      state.isLoading = false;
      state.agency = null;
      toast.error(action?.payload?.message);
    },

    // get agency details
    [getAgencyDetails.pending]: (state) => {
      state.isLoading = true;
      state.agency = null;
    },
    [getAgencyDetails.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.agency = action.payload.data;
    },
    [getAgencyDetails.rejected]: (state, action) => {
      state.isLoading = false;
      state.agency = null;
      toast.error(action?.payload?.message);
    },

    // get agency users
    [getAgencyUsers.pending]: (state) => {
      state.isUserLoading = true;
      state.users = null;
    },
    [getAgencyUsers.fulfilled]: (state, action) => {
      state.isUserLoading = false;
      state.users = action.payload.data;
    },
    [getAgencyUsers.rejected]: (state, action) => {
      state.isUserLoading = false;
      state.users = null;
      toast.error(action?.payload?.message);
    },

    // update agency payment status
    [updateAgencyPaymentStatus.pending]: (state) => {
      state.isPaymentStatusLoading = true;
    },
    [updateAgencyPaymentStatus.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        paymentStatus: action.payload.data.paymentStatus,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.isPaymentStatusLoading = false;
    },
    [updateAgencyPaymentStatus.rejected]: (state, action) => {
      state.isPaymentStatusLoading = false;
      toast.error(action?.payload?.message);
    },

    // get agency settings
    [getAgencySettings.pending]: (state) => {
      state.settingsError = null;
      if (!state.agencySettings) {
        state.isSettingsLoading = true;
        state.isPaymentStatusLoading = true;
        state.isAgencyStatusLoading = true;
        state.isPaymentGatewayLoading = true;
        state.isBitpayPaymentLoading = true;
        state.isBnplLoading = true;
        state.isManageLocationLoading = true;
        state.isSameDayAllowBookingLoading = true;
        state.isUserLimitLoading = true;
        state.isHotelVendorLoading = true;
      }
    },
    [getAgencySettings.fulfilled]: (state, action) => {
      state.isSettingsLoading = false;
      state.settingsError = null;
      state.isPaymentStatusLoading = false;
      state.isAgencyStatusLoading = false;
      state.isPaymentGatewayLoading = false;
      state.isBitpayPaymentLoading = false;
      state.isBnplLoading = false;
      state.isManageLocationLoading = false;
      state.isSameDayAllowBookingLoading = false;
      state.isUserLimitLoading = false;
      state.isHotelVendorLoading = false;
      const d = action.payload.data;
      state.agencySettings = {
        agencySettings: {
          ...d,
          limit: [
            { title: 'Sales Agent Limit', limit: d.sales_agents_limit ?? 0 },
            { title: 'Traveller Limit', limit: d.customers_limit ?? 0 },
          ],
        },
      };
    },
    [getAgencySettings.rejected]: (state, action) => {
      state.isSettingsLoading = false;
      state.settingsError = action?.payload?.message || 'Failed to load agency settings';
      state.isPaymentStatusLoading = false;
      state.isAgencyStatusLoading = false;
      state.isPaymentGatewayLoading = false;
      state.isBitpayPaymentLoading = false;
      state.isBnplLoading = false;
      state.isManageLocationLoading = false;
      state.isSameDayAllowBookingLoading = false;
      state.isUserLimitLoading = false;
      state.agencySettings = null;
      state.isHotelVendorLoading = false;
      toast.error(action?.payload?.message);
    },

    // update agency status
    [updateAgencyStatus.pending]: (state) => {
      state.isAgencyStatusLoading = true;
    },
    [updateAgencyStatus.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        status: action.payload.data.status,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.isAgencyStatusLoading = false;
    },
    [updateAgencyStatus.rejected]: (state, action) => {
      state.isAgencyStatusLoading = false;
      toast.error(action?.payload?.message);
    },

    // update agency disallow same day
    [updateDisallowSameDay.pending]: (state) => {
      state.isSameDayAllowBookingLoading = true;
    },
    [updateDisallowSameDay.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        disallowSameDayBooking: action.payload.data.disallowSameDayBooking,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.isSameDayAllowBookingLoading = false;
    },
    [updateDisallowSameDay.rejected]: (state, action) => {
      state.isSameDayAllowBookingLoading = false;
      toast.error(action?.payload?.message);
    },

    // update agency disallow same day
    [updateApiAccess.pending]: (state) => {
      state.isApiAccessLoading = true;
    },
    [updateApiAccess.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        apiAccess: action.payload.data.apiAccess,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.isApiAccessLoading = false;
    },
    [updateApiAccess.rejected]: (state) => {
      state.isApiAccessLoading = false;
    },

    [updateAgencySettings.pending]: (state) => {
      state.isUserLimitLoading = true;
    },
    [updateAgencySettings.fulfilled]: (state, action) => {
      state.isUserLimitLoading = false;
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        limit: action.payload.data.limit,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
    },
    [updateAgencySettings.rejected]: (state) => {
      state.isUserLimitLoading = false;
    },

    // cancel agency subscription
    [cancelAgencySubscription.pending]: (state) => {
      state.isCancelSubscriptionLoading = true;
    },
    [cancelAgencySubscription.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings?.agencySettings,
        subscriptionStatus:
          action.payload?.data?.subscriptionStatus ?? agencySettings?.agencySettings?.subscriptionStatus,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.isCancelSubscriptionLoading = false;
      toast.success(action?.payload?.message || 'Subscription cancellation scheduled successfully');
    },
    [cancelAgencySubscription.rejected]: (state, action) => {
      state.isCancelSubscriptionLoading = false;
      toast.error(action?.payload?.message);
    },

    // cancel agency trial subscription
    [cancelTrialSubscription.pending]: (state) => {
      state.isCancelSubscriptionLoading = true;
    },
    [cancelTrialSubscription.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings?.agencySettings,
        subscriptionStatus:
          action.payload?.data?.subscriptionStatus ?? agencySettings?.agencySettings?.subscriptionStatus,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.isCancelSubscriptionLoading = false;
      toast.success(action?.payload?.message || 'Trial ended successfully');
    },
    [cancelTrialSubscription.rejected]: (state, action) => {
      state.isCancelSubscriptionLoading = false;
      toast.error(action?.payload?.message);
    },

    // update agency user status
    [updateAgencyUserStatus.pending]: (state) => {
      state.isUserUpdateLoading = true;
    },
    [updateAgencyUserStatus.fulfilled]: (state, action) => {
      const { users } = state;
      const data = users.users.map((el) => {
        if (el?._id === action.payload.data.user?._id) {
          return {
            ...el,
            ...action.payload.data.user,
          };
        }
        return el;
      });
      state.users = { users: data, count: users?.count };
      state.isUserUpdateLoading = false;
    },
    [updateAgencyUserStatus.rejected]: (state, action) => {
      state.isUserUpdateLoading = false;
      toast.error(action?.payload?.message);
    },

    // resend verification email
    [resendVerificationEmail.pending]: (state) => {
      state.isUserUpdateLoading = true;
    },
    [resendVerificationEmail.fulfilled]: (state, action) => {
      state.isUserUpdateLoading = false;
      toast.success(action.payload.message);
    },
    [resendVerificationEmail.rejected]: (state, action) => {
      state.isUserUpdateLoading = false;
      toast.error(action?.payload?.message);
    },

    // agency user Login
    [adminUserLogin.pending]: (state) => {
      state.isUserUpdateLoading = true;
    },
    [adminUserLogin.fulfilled]: (state, action) => {
      state.isUserUpdateLoading = false;
      toast.success(action.payload.message);
    },
    [adminUserLogin.rejected]: (state, action) => {
      state.isUserUpdateLoading = false;
      toast.error(action?.payload?.message);
    },

    // deleteAgency
    [deleteAgency.pending]: (state) => {
      state.isLoading = true;
    },
    [deleteAgency.fulfilled]: (state, action) => {
      state.isLoading = false;
      const { agency } = state;
      const deletedId = action.payload.data?.account_id || action.payload.data?.id;
      const data = agency.agencies.filter((el) => el.id !== deletedId);
      state.agency = { ...agency, agencies: data, agencyCount: agency?.agencyCount - 1 };
      toast.success(action.payload.message);
    },
    [deleteAgency.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action?.payload?.message);
    },

    // update flight ticket permission
    [updateTicketingPermission.pending]: (state) => {
      state.isFlightTicketingLoading = true;
    },
    [updateTicketingPermission.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        isFlightTicketingAllowed: action.payload.data.isFlightTicketingAllowed,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.isFlightTicketingLoading = false;
    },
    [updateTicketingPermission.rejected]: (state, action) => {
      state.isFlightTicketingLoading = false;
      toast.error(action?.payload?.message);
    },

    // resend verify agency email
    [resendVerificationAgencyEmail.fulfilled]: (state, action) => {
      toast.success(action.payload.message);
    },

    [resendVerificationAgencyEmail.rejected]: (state, action) => {
      toast.error(action?.payload?.message);
    },

    // get agent details
    [getAgentDetails.pending]: (state) => {
      state.isLoading = true;
      state.agentDetails = null;
    },
    [getAgentDetails.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.agentDetails = action.payload.data;
    },
    [getAgentDetails.rejected]: (state, action) => {
      state.isLoading = false;
      state.agentDetails = null;
      toast.error(action?.payload?.message);
    },

    [getAgentAssociateClientData.pending]: (state) => {
      state.isLoading = true;
      state.agentDetails = null;
    },
    [getAgentAssociateClientData.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.agentDetails = action.payload.data;
    },
    [getAgentAssociateClientData.rejected]: (state, action) => {
      state.isLoading = false;
      state.agentDetails = null;
      toast.error(action?.payload?.message || 'Failed to load agent details');
    },

    // get associated clients
    [getAssociatedAgentClients.pending]: (state) => {
      state.isLoading = true;
      state.associatedClients = null;
    },
    [getAssociatedAgentClients.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.associatedClients = action.payload.data;
    },
    [getAssociatedAgentClients.rejected]: (state, action) => {
      state.isLoading = false;
      state.associatedClients = null;
      toast.error(action?.payload?.message);
    },

    [getAssociateClientList.pending]: (state) => {
      state.isUserLoading = true;
      state.associatedClients = null;
    },
    [getAssociateClientList.fulfilled]: (state, action) => {
      state.isUserLoading = false;
      state.associatedClients = action.payload.data;
    },
    [getAssociateClientList.rejected]: (state, action) => {
      state.isUserLoading = false;
      state.associatedClients = null;
      toast.error(action?.payload?.message || 'Failed to load associate client list');
    },

    [updateAgencyPaymentGateway.pending]: (state) => {
      state.isPaymentGatewayLoading = true;
    },
    [updateAgencyPaymentGateway.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        paymentGatewayList: action.payload.data.paymentGatewayList,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.isPaymentGatewayLoading = false;
    },
    [updateAgencyPaymentGateway.rejected]: (state, action) => {
      state.isPaymentGatewayLoading = false;
      toast.error(action?.payload?.message);
    },

    [updateBitpayPaymentAllowed.pending]: (state) => {
      state.isBitpayPaymentLoading = true;
    },
    [updateBitpayPaymentAllowed.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        isBitpayPaymentAllowed: action.payload.data.isBitpayPaymentAllowed,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.isBitpayPaymentLoading = false;
    },
    [updateBitpayPaymentAllowed.rejected]: (state, action) => {
      state.isBitpayPaymentLoading = false;
      toast.error(action?.payload?.message);
    },
    [updateAgencySettingsData.pending]: (state) => {
      state.isAgencySettingsDataLoading = true;
    },
    [updateAgencySettingsData.fulfilled]: (state, action) => {
      state.isAgencySettingsDataLoading = false;
      console.log(action.payload.data, 'action.payload.data');
      const data = {
        ...state.agencySettings.agencySettings,
        ...action.payload.data,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
    },
    [updateAgencySettingsData.rejected]: (state, action) => {
      state.isAgencySettingsDataLoading = false;
      toast.error(action?.payload?.message);
    },

    [updateBNPLPaymentAllowed.pending]: (state) => {
      state.isBnplLoading = true;
    },
    [updateBNPLPaymentAllowed.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        isBnplAllowed: action.payload.data.isBnplAllowed,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.isBnplLoading = false;
    },
    [updateBNPLPaymentAllowed.rejected]: (state, action) => {
      state.isBnplLoading = false;
      toast.error(action?.payload?.message);
    },

    [updateManageLocation.pending]: (state) => {
      state.isManageLocationLoading = true;
    },
    [updateManageLocation.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        manageLocation: action.payload.data.manageLocation,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.isManageLocationLoading = false;
    },
    [updateManageLocation.rejected]: (state, action) => {
      state.isManageLocationLoading = false;
      toast.error(action?.payload?.message);
    },

    [updateAgencyHotelVendor.pending]: (state) => {
      state.isHotelVendorLoading = true;
    },
    [updateAgencyHotelVendor.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        hotelVendorsList: action.payload.data.hotelVendorsList,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.isHotelVendorLoading = false;
    },
    [updateAgencyHotelVendor.rejected]: (state, action) => {
      state.isHotelVendorLoading = false;
      toast.error(action?.payload?.message);
    },

    [updateDisplayTotalPriceWithAdditionalCharges.pending]: (state) => {
      state.displayTotalPriceWithAdditionalChargesLoading = true;
    },
    [updateDisplayTotalPriceWithAdditionalCharges.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        displayTotalWithAdditionalCharges: action.payload.data.displayTotalWithAdditionalCharges,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.displayTotalPriceWithAdditionalChargesLoading = false;
      toast.success(action?.payload?.message);
    },
    [updateDisplayTotalPriceWithAdditionalCharges.rejected]: (state, action) => {
      state.displayTotalPriceWithAdditionalChargesLoading = false;
      toast.error(action?.payload?.message);
    },

    // update agency OTP signup
    [updateOTPSignupStatus.pending]: (state) => {
      state.OTPSignupEnableLoading = true;
    },
    [updateOTPSignupStatus.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings?.agencySettings,
        OTPSignupEnable: action.payload.data.OTPSignupEnable,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.OTPSignupEnableLoading = false;
    },
    [updateOTPSignupStatus.rejected]: (state, action) => {
      state.OTPSignupEnableLoading = false;
      toast.error(action?.payload?.message);
    },

    [updatHideHeaderStatus.pending]: (state) => {
      state.hideHeaderEnableLoading = true;
    },
    [updatHideHeaderStatus.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        hideHeaderforDealProposal: action.payload.data.hideHeaderforDealProposal,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.hideHeaderEnableLoading = false;
    },
    [updatHideHeaderStatus.rejected]: (state, action) => {
      state.hideHeaderEnableLoading = false;
      toast.error(action?.payload?.message);
    },

    [updateExranetStatus.pending]: (state) => {
      state.extranetLoading = true;
    },
    [updateExranetStatus.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        extranetEnabled: action.payload.data.extranetEnabled,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.extranetLoading = false;
    },
    [updateExranetStatus.rejected]: (state, action) => {
      state.extranetLoading = false;
      toast.error(action?.payload?.message);
    },

    [updateHideDealOrProposalOption.pending]: (state) => {
      state.hideDealOrProposalLoading = true;
    },
    [updateHideDealOrProposalOption.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        hideDealOrProposalOption: action.payload.data.hideDealOrProposalOption,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.hideDealOrProposalLoading = false;
    },
    [updateHideDealOrProposalOption.rejected]: (state, action) => {
      state.hideDealOrProposalLoading = false;
      toast.error(action?.payload?.message);
    },

    [updateAllowNonLoginFlow.pending]: (state) => {
      state.allowNonLoginFlowLoading = true;
    },
    [updateAllowNonLoginFlow.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        allowNonLoginFlow: action.payload.data.allowNonLoginFlow,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.allowNonLoginFlowLoading = false;
    },
    [updateAllowNonLoginFlow.rejected]: (state, action) => {
      state.allowNonLoginFlowLoading = false;
      toast.error(action?.payload?.message);
    },

    [updateAgencyViewVendorName.pending]: (state) => {
      state.agencyViewVendorNameLoading = true;
    },
    [updateAgencyViewVendorName.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        displayVendorName: action.payload.data.displayVendorName,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.agencyViewVendorNameLoading = false;
    },
    [updateAgencyViewVendorName.rejected]: (state, action) => {
      state.agencyViewVendorNameLoading = false;
      toast.error(action?.payload?.message);
    },
    [updateAgencyEnableAllNewUSer.pending]: (state) => {
      state.enableAllNewUserLoading = true;
    },
    [updateAgencyEnableAllNewUSer.fulfilled]: (state, action) => {
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        enableUserVerifactionFlow: action.payload.data.enableUserVerifactionFlow,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.enableAllNewUserLoading = false;
    },
    [updateAgencyEnableAllNewUSer.rejected]: (state, action) => {
      state.enableAllNewUserLoading = false;
      toast.error(action?.payload?.message);
    },

    [updateResortSetting.pending]: (state) => {
      state.isLoading = true;
    },
    [updateResortSetting.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.agencySettings = {
        ...state.agencySettings,
        agencySettings: {
          ...state.agencySettings.agencySettings,
          isResortsEnable: action.payload.data.isEnabled,
        },
      };
      toast.success(action?.payload?.message);
    },
    [updateResortSetting.rejected]: (state, action) => {
      state.isLoading = false;
      toast.error(action?.payload?.message);
    },
    [updateCancellationEmail.pending]: (state) => {
      state.isLoading = true;
    },
    [updateCancellationEmail.fulfilled]: (state, action) => {
      state.isLoading = false;
      const { agencySettings } = state;
      const data = {
        ...agencySettings.agencySettings,
        cancellationDisplayOnEmail: action.payload.data.cancellationDisplayOnEmail,
      };
      state.agencySettings = {
        agencySettings: {
          ...data,
        },
      };
      state.cancellationDisplayOnEmail = false;
      toast.success(action?.payload?.message);
    },
    [updateCancellationEmail.rejected]: (state, action) => {
      state.cancellationDisplayOnEmail = false;
      state.isLoading = false;
      toast.error(action?.payload?.message);
    },

    [getHotelVendorList.pending]: (state, action) => {
      const productType = action.meta?.arg?.product_type || 'HOTEL';
      state.isAccountVendorsLoading[productType] = true;
      state.isHotelVendorListLoading = productType === 'HOTEL';
    },
    [getHotelVendorList.fulfilled]: (state, action) => {
      const productType = action.payload._productType || 'HOTEL';
      state.isAccountVendorsLoading[productType] = false;
      state.accountVendors[productType] = action.payload.data || [];
      if (productType === 'HOTEL') {
        state.isHotelVendorListLoading = false;
        state.hotelVendorList = action.payload.data || [];
      }
    },
    [getHotelVendorList.rejected]: (state, action) => {
      const productType = action.meta?.arg?.product_type || 'HOTEL';
      state.isAccountVendorsLoading[productType] = false;
      if (productType === 'HOTEL') state.isHotelVendorListLoading = false;
      toast.error(action?.payload?.message || 'Failed to load account vendors');
    },

    [updateAgencyHotelVendors.pending]: (state, action) => {
      const { vendor_id, enabled, product_type } = action.meta?.arg || {};
      state.updatingVendorId = vendor_id;
      state.updatingAccountVendor = { id: vendor_id, product_type };
      const vendors = state.accountVendors[product_type] || [];
      state.accountVendorsRollback = { product_type, vendors: vendors.map((v) => ({ ...v })) };
      state.accountVendors[product_type] = vendors.map((v) => (v.id === vendor_id ? { ...v, enabled } : v));
      if (product_type === 'HOTEL') {
        state.hotelVendorList = state.accountVendors.HOTEL;
      }
    },
    [updateAgencyHotelVendors.fulfilled]: (state, action) => {
      state.updatingVendorId = null;
      state.updatingAccountVendor = null;
      state.accountVendorsRollback = null;
      toast.success(action?.payload?.message);
    },
    [updateAgencyHotelVendors.rejected]: (state, action) => {
      const rollback = state.accountVendorsRollback;
      if (rollback?.product_type) {
        state.accountVendors[rollback.product_type] = rollback.vendors;
        if (rollback.product_type === 'HOTEL') {
          state.hotelVendorList = rollback.vendors;
        }
      }
      state.updatingVendorId = null;
      state.updatingAccountVendor = null;
      state.accountVendorsRollback = null;
      toast.error(action?.payload?.message || 'Failed to update account vendor');
    },

    [updateAccountAdminSettings.pending]: (state, action) => {
      const { settings, settingKey } = action.meta?.arg || {};
      state.isAdminSettingsUpdating = true;
      state.updatingSettingKey = settingKey || null;
      if (state.agencySettings?.agencySettings && settings) {
        state.settingsRollbackSnapshot = { ...state.agencySettings.agencySettings };
        state.agencySettings = {
          agencySettings: {
            ...state.agencySettings.agencySettings,
            ...settings,
          },
        };
      }
    },
    [updateAccountAdminSettings.fulfilled]: (state, action) => {
      state.isAdminSettingsUpdating = false;
      state.updatingSettingKey = null;
      state.settingsRollbackSnapshot = null;
      const responseData = action.payload.data;
      const sentSettings = action.payload._sentSettings;
      const source = responseData || sentSettings;
      if (source && state.agencySettings?.agencySettings) {
        const prev = state.agencySettings.agencySettings;
        state.agencySettings = {
          agencySettings: {
            ...prev,
            ...source,
            limit: [
              { title: 'Sales Agent Limit', limit: source.sales_agents_limit ?? prev.limit?.[0]?.limit ?? 0 },
              { title: 'Traveller Limit', limit: source.customers_limit ?? prev.limit?.[1]?.limit ?? 0 },
            ],
          },
        };
      }
      const accountId = action.meta?.arg?.account_id;
      if (sentSettings?.classification !== undefined && accountId && state.agency?.agencies) {
        state.agency.agencies = state.agency.agencies.map((item) =>
          item.id === accountId ? { ...item, classification: sentSettings.classification } : item
        );
      }
      toast.success(action?.payload?.message);
    },
    [updateAccountAdminSettings.rejected]: (state, action) => {
      state.isAdminSettingsUpdating = false;
      state.updatingSettingKey = null;
      if (state.settingsRollbackSnapshot && state.agencySettings) {
        state.agencySettings = {
          agencySettings: state.settingsRollbackSnapshot,
        };
      }
      state.settingsRollbackSnapshot = null;
      toast.error(action?.payload?.message || 'Failed to update settings');
    },

    [getMerchant.pending]: (state, action) => {
      state.isMerchantLoading = true;
      const accountId = action.meta?.arg?.account_id;
      if (state.merchantAccountId !== accountId) {
        state.isMerchantFetched = false;
        state.merchantDetails = null;
        state.merchantAccountId = accountId || null;
      }
    },
    [getMerchant.fulfilled]: (state, action) => {
      state.isMerchantLoading = false;
      state.isMerchantFetched = true;
      state.merchantAccountId = action.meta?.arg?.account_id || state.merchantAccountId;
      state.merchantDetails = action.payload?.data ?? null;
    },
    [getMerchant.rejected]: (state, action) => {
      state.isMerchantLoading = false;
      state.isMerchantFetched = true;
      state.merchantAccountId = action.meta?.arg?.account_id || state.merchantAccountId;
      state.merchantDetails = null;
      toast.error(action?.payload?.message || 'Failed to fetch merchant details');
    },

    [updateMerchant.pending]: (state) => {
      state.isMerchantUpdating = true;
    },
    [updateMerchant.fulfilled]: (state, action) => {
      state.isMerchantUpdating = false;
      const sent = action.payload?._sentData;
      if (sent) {
        state.merchantDetails = {
          ...(state.merchantDetails || {}),
          ...(sent.merchant_name != null ? { merchant_name: sent.merchant_name } : {}),
          ...(sent.merchant_dns_subdomain != null
            ? { merchant_dns_subdomain: sent.merchant_dns_subdomain }
            : {}),
        };
      }
      toast.success(action?.payload?.message || 'Merchant details updated successfully');
    },
    [updateMerchant.rejected]: (state, action) => {
      state.isMerchantUpdating = false;
      const payload = action?.payload;
      const errorMessage =
        payload?.error ||
        payload?.message ||
        (typeof payload === 'string' ? payload : null) ||
        'Failed to update merchant details';
      toast.error(errorMessage);
    },

    [getAccountSubscription.pending]: (state) => {
      if (!state.accountSubscription) {
        state.isAccountSubscriptionLoading = true;
      }
    },
    [getAccountSubscription.fulfilled]: (state, action) => {
      state.isAccountSubscriptionLoading = false;
      state.accountSubscription = action.payload?.data ?? action.payload;
    },
    [getAccountSubscription.rejected]: (state, action) => {
      state.isAccountSubscriptionLoading = false;
      state.accountSubscription = null;
      toast.error(action?.payload?.message || 'Failed to fetch subscription details');
    },

    [getSubscriptionLimits.pending]: (state) => {
      if (!state.subscriptionLimits) {
        state.isSubscriptionLimitsLoading = true;
      }
    },
    [getSubscriptionLimits.fulfilled]: (state, action) => {
      state.isSubscriptionLimitsLoading = false;
      state.subscriptionLimits = action.payload.data;
    },
    [getSubscriptionLimits.rejected]: (state, action) => {
      state.isSubscriptionLimitsLoading = false;
      state.subscriptionLimits = null;
      toast.error(action?.payload?.message || 'Failed to fetch subscription limits');
    },

    [updateSubscriptionLimits.pending]: (state, action) => {
      const sent = action.meta?.arg;
      state.isSubscriptionLimitsUpdating = true;
      if (state.subscriptionLimits && sent) {
        state.subscriptionLimitsRollback = { ...state.subscriptionLimits };
        if (state.subscriptionLimits.customers_limit !== sent.customers_limit) {
          state.updatingLimitKey = 'customers_limit';
        } else if (state.subscriptionLimits.sales_agents_limit !== sent.sales_agents_limit) {
          state.updatingLimitKey = 'sales_agents_limit';
        }
        if (sent.customers_limit != null) state.subscriptionLimits.customers_limit = sent.customers_limit;
        if (sent.sales_agents_limit != null) state.subscriptionLimits.sales_agents_limit = sent.sales_agents_limit;
      }
    },
    [updateSubscriptionLimits.fulfilled]: (state, action) => {
      state.isSubscriptionLimitsUpdating = false;
      state.updatingLimitKey = null;
      state.subscriptionLimitsRollback = null;
      toast.success(action?.payload?.message || 'Limits updated successfully');
    },
    [updateSubscriptionLimits.rejected]: (state, action) => {
      state.isSubscriptionLimitsUpdating = false;
      state.updatingLimitKey = null;
      if (state.subscriptionLimitsRollback) {
        state.subscriptionLimits = state.subscriptionLimitsRollback;
      }
      state.subscriptionLimitsRollback = null;
      toast.error(action?.payload?.message || 'Failed to update limits');
    },

    [getAdminCommissionSummary.pending]: (state) => {
      state.isCommissionSummaryLoading = true;
    },
    [getAdminCommissionSummary.fulfilled]: (state, action) => {
      state.isCommissionSummaryLoading = false;
      state.commissionSummary = action.payload.data;
    },
    [getAdminCommissionSummary.rejected]: (state, action) => {
      state.isCommissionSummaryLoading = false;
      state.commissionSummary = null;
      toast.error(action?.payload?.message || 'Failed to load commission summary');
    },

    [getAccountUserStats.pending]: (state) => {
      state.isUserStatsLoading = true;
    },
    [getAccountUserStats.fulfilled]: (state, action) => {
      state.isUserStatsLoading = false;
      state.userStats = action.payload.data;
    },
    [getAccountUserStats.rejected]: (state, action) => {
      state.isUserStatsLoading = false;
      state.userStats = null;
      toast.error(action?.payload?.message || 'Failed to load user stats');
    },

    [getClientUsers.pending]: (state) => {
      state.isClientUsersLoading = true;
    },
    [getClientUsers.fulfilled]: (state, action) => {
      state.isClientUsersLoading = false;
      state.clientUsers = action.payload.data;
    },
    [getClientUsers.rejected]: (state, action) => {
      state.isClientUsersLoading = false;
      state.clientUsers = null;
      toast.error(action?.payload?.message || 'Failed to load users');
    },

    [updateUserStatus.pending]: (state) => {
      state.isUserUpdateLoading = true;
    },
    [updateUserStatus.fulfilled]: (state, action) => {
      state.isUserUpdateLoading = false;
      const userId = action.payload._id;
      const newStatus = action.payload._newStatus;
      if (state.clientUsers) {
        const listKey = state.clientUsers.users
          ? 'users'
          : state.clientUsers.clients
          ? 'clients'
          : state.clientUsers.agents
          ? 'agents'
          : null;
        if (listKey) {
          state.clientUsers[listKey] = state.clientUsers[listKey].map((el) =>
            el?.id === userId || el?._id === userId ? { ...el, status: newStatus.toUpperCase() } : el
          );
        }
      }
      toast.success(action?.payload?.message || 'Status updated successfully');
    },
    [updateUserStatus.rejected]: (state, action) => {
      state.isUserUpdateLoading = false;
      toast.error(action?.payload?.message || 'Failed to update status');
    },

    [getAgentUsers.pending]: (state) => {
      state.isClientUsersLoading = true;
    },
    [getAgentUsers.fulfilled]: (state, action) => {
      state.isClientUsersLoading = false;
      state.clientUsers = action.payload.data;
    },
    [getAgentUsers.rejected]: (state, action) => {
      state.isClientUsersLoading = false;
      state.clientUsers = null;
      toast.error(action?.payload?.message || 'Failed to load agents');
    },
  },
});

export default agencySlice.reducer;
